import { useState, useEffect } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { api, type FamilyMember, type ExpenseCategory } from '@/api/client';

interface FiltersBarProps {
  taxYear: number | undefined;
  status: 'Unreimbursed' | 'PartiallyReimbursed' | 'FullyReimbursed' | undefined;
  familyMemberId: string | undefined;
  categoryId: string | undefined;
  searchQuery: string;
  onTaxYearChange: (year: number | undefined) => void;
  onStatusChange: (status: 'Unreimbursed' | 'PartiallyReimbursed' | 'FullyReimbursed' | undefined) => void;
  onFamilyMemberChange: (id: string | undefined) => void;
  onCategoryChange: (id: string | undefined) => void;
  onSearchChange: (query: string) => void;
  onClearFilters: () => void;
}

export function FiltersBar({
  taxYear,
  status,
  familyMemberId,
  categoryId,
  searchQuery,
  onTaxYearChange,
  onStatusChange,
  onFamilyMemberChange,
  onCategoryChange,
  onSearchChange,
  onClearFilters,
}: FiltersBarProps) {
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadFamilyMembers();
    loadCategories();
  }, []);

  const loadFamilyMembers = async () => {
    try {
      const data = await api.getFamilyMembers();
      if (data) setFamilyMembers(data);
    } catch (err) {
      console.error('Error loading family members:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await api.getExpenseCategories();
      if (data) setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  // Generate last 10 years for tax year filter
  const currentYear = new Date().getFullYear();
  const taxYears = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const hasActiveFilters = taxYear !== undefined || status || familyMemberId || categoryId || searchQuery;

  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] p-4">
      <div className="space-y-3">
        {/* Top row: Search and Toggle Filters */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
            <input
              type="text"
              placeholder="Search by provider or description..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full h-10 pl-10 pr-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D]"
            />
          </div>

          {/* Toggle Filters Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`h-10 px-4 flex items-center gap-2 text-[14px] border rounded transition-colors ${
              showFilters
                ? 'bg-[#1B365D] text-white border-[#1B365D]'
                : 'bg-white text-[#4A5568] border-[#E2E8F0] hover:bg-[#F7FAFC]'
            }`}
          >
            <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            <span>Filters</span>
            {hasActiveFilters && !showFilters && (
              <span className="ml-1 px-2 py-0.5 bg-[#C9A227] text-white text-[12px] rounded-full">
                {[taxYear, status, familyMemberId, categoryId, searchQuery].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="h-10 px-3 flex items-center gap-2 text-[14px] text-red-600 border border-red-200 rounded hover:bg-red-50 transition-colors"
              title="Clear all filters"
            >
              <X size={16} />
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </div>

        {/* Filter Dropdowns (Collapsible) */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-[#E2E8F0]">
            {/* Tax Year Filter */}
            <div>
              <label className="block text-[12px] font-medium text-[#4A5568] mb-1.5">Tax Year</label>
              <select
                value={taxYear ?? ''}
                onChange={(e) => onTaxYearChange(e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full h-9 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] bg-white"
              >
                <option value="">All Years</option>
                {taxYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Reimbursement Status Filter */}
            <div>
              <label className="block text-[12px] font-medium text-[#4A5568] mb-1.5">Status</label>
              <select
                value={status ?? ''}
                onChange={(e) => onStatusChange(e.target.value as any || undefined)}
                className="w-full h-9 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] bg-white"
              >
                <option value="">All Statuses</option>
                <option value="Unreimbursed">Unreimbursed</option>
                <option value="PartiallyReimbursed">Partially Reimbursed</option>
                <option value="FullyReimbursed">Fully Reimbursed</option>
              </select>
            </div>

            {/* Family Member Filter */}
            <div>
              <label className="block text-[12px] font-medium text-[#4A5568] mb-1.5">Family Member</label>
              <select
                value={familyMemberId ?? ''}
                onChange={(e) => onFamilyMemberChange(e.target.value || undefined)}
                className="w-full h-9 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] bg-white"
                disabled={familyMembers.length === 0}
              >
                <option value="">All Members</option>
                {familyMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Expense Category Filter */}
            <div>
              <label className="block text-[12px] font-medium text-[#4A5568] mb-1.5">Category</label>
              <select
                value={categoryId ?? ''}
                onChange={(e) => onCategoryChange(e.target.value || undefined)}
                className="w-full h-9 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] bg-white"
                disabled={categories.length === 0}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
